import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import autoTable from "jspdf-autotable";
import { Button, Modal, Form } from "react-bootstrap";
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  LayersControl,
  Marker,
  Popup,
  Polyline,
  useMap,
} from "react-leaflet";
import { Box, IconButton } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { motion, AnimatePresence } from "framer-motion";
import DescriptionIcon from "@mui/icons-material/Description";
import CircularProgress from "@mui/material/CircularProgress";
import "leaflet/dist/leaflet.css";
import "./ImpactForecast.css";
import domtoimage from "dom-to-image-more";
import { FaCircleInfo } from "react-icons/fa6";
import RainfallForecast from "../../../common/RainfallForecast/RainfallForecast.js";
import ImpactedArea from "../ImpactForecast/ImpactedArea.js";
import OceanWave from "../ImpactForecast/OceanWave.js";
import Observation from "../ImpactForecast/Observation.js";
import L from "leaflet";
import axios from 'axios';
import moment from 'moment-timezone';
import jsPDF from "jspdf";
import { translations, rainfallCharacteristicsTranslations, characteristicColors } from "./Translations.js";
import { getForecastDate } from "./dateUtils.js";
import { MapHomeControl } from "../../../common/MapControls/MapControls";

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

const INITIAL_VIEW_STATE = {
  longitude: 125.7275,
  latitude: -8.8742,
  zoom: 8,
};

const getCharacteristic = (rainfall) => {
  const rain = parseFloat(rainfall);
  if (isNaN(rain)) return "N/A";
  if (rain > -0.1 && rain <= 20) return "NoRisk";
  if (rain > 20 && rain <= 50) return "Low";
  if (rain > 50 && rain <= 100) return "Medium";
  if (rain > 100) return "High";
  return "N/A";
};

const getWaveCategory = (waveHeight) => {
  if (waveHeight <= 1.0) {
    return { label: "No Risk", color: [148, 203, 85] };
  }
  if (waveHeight <= 1.5) {
    return { label: "Low", color: [243, 235, 18] };
  }
  if (waveHeight <= 2.0) {
    return { label: "Medium", color: [243, 155, 58] };
  }
  return { label: "High", color: [230, 45, 58] };
};

const fetchAllForecastMapData = async (type) => {
  let baseUrl;
  switch (type) {
    case "municipality":
      baseUrl = `${backendUrl}ecmwf/forecast/10_days_rainfall_municipality/`;
      break;
    case "sub_municipality":
      baseUrl = `${backendUrl}ecmwf/forecast/10_days_rainfall_sub_municipality/`;
      break;
    case "suco":
      baseUrl = `${backendUrl}ecmwf/forecast/10_days_rainfall_suco/`;
      break;
    default:
      throw new Error("Invalid type for fetching forecast data");
  }

  const endpoint = baseUrl;

  try {
    const res = await fetch(endpoint, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!res.ok) {
      const errorText = await res.text();
      throw new Error(
        `HTTP error! Status: ${res.status}, Message: ${errorText}`
      );
    }

    const data = await res.json();
    return data || [];
  } catch (error) {
    console.error(`Error fetching all ${type} forecast data:`, error);
    return [];
  }
};

const processForecastDataForDays = (allForecasts, targetDates) => {
  const processedData = {};
  targetDates.forEach((date) => {
    processedData[date] = {};
  });

  allForecasts.forEach((item) => {
    const code = item.adm_code || item.sub_adm_code;
    const itemForecastDate = item.fcst_date;

    if (targetDates.includes(itemForecastDate)) {
      processedData[itemForecastDate][code] = item.rainfall;
    }
  });
  return processedData;
};

const MapBoundsUpdater = ({ bounds }) => {
  const map = useMap();

  useEffect(() => {
    if (bounds) {
      map.flyToBounds(bounds);
    }
  }, [map, bounds]);

  return null;
};

const ZoomToFeature = ({ feature }) => {
  const map = useMap();
  useEffect(() => {
    if (feature) {
      const geojsonLayer = L.geoJSON(feature);
      map.flyToBounds(geojsonLayer.getBounds(), { padding: [20, 20] });
    }
  }, [feature, map]);
  return null;
};

const MapViewAdjuster = ({ features, map }) => {
  useEffect(() => {
    if (features && features.length > 0 && map) {
      const bounds = L.latLngBounds(
        features
          .map((feature) => {
            if (feature.geometry.type === "Point") {
              return [
                feature.geometry.coordinates[1],
                feature.geometry.coordinates[0],
              ];
            } else if (feature.geometry.type === "Polygon") {
              return feature.geometry.coordinates[0].map((coord) => [
                coord[1],
                coord[0],
              ]);
            } else if (feature.geometry.type === "LineString") {
              return feature.geometry.coordinates.map((coord) => [
                coord[1],
                coord[0],
              ]);
            }
            return null;
          })
          .flat()
      );

      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [features, map]);

  return null;
};

const InfoButton = ({ onClick }) => {
  // Leaflet's own drag/click handling swallows clicks on overlay controls
  // unless propagation to the map is explicitly disabled on the DOM node.
  const btnRef = useCallback((node) => {
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);
  return (
    <div
      ref={btnRef}
      className="info-button"
      style={{
        position: "absolute",
        bottom: "20px",
        left: "12px",
        padding: "8px",
        borderRadius: "8px",
        fontSize: "15px",
        cursor: "pointer",
        background: "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(10px) brightness(1.1)",
        WebkitBackdropFilter: "blur(10px) brightness(1.1)",
        border: "1px solid rgba(0, 0, 0, 0.08)",
        color: "#333333",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        transition: "all 0.2s ease",
        zIndex: 1000,
      }}
      onClick={onClick}
    >
      <FaCircleInfo />
    </div>
  );
};

const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

const addBanner = (pdf, bannerImage, sideMargin, topMargin, pageWidth) => {
  const bannerHeight = 20;
  const bannerWidth = pageWidth - 2 * sideMargin;
  if (bannerImage) {
    pdf.addImage(
      bannerImage,
      "PNG",
      sideMargin,
      topMargin,
      bannerWidth,
      bannerHeight
    );
  }
  return topMargin + bannerHeight + 5;
};

const ImpactForecast = () => {
  const [info, setInfo] = useState(false);
  const infoRef = useRef(null);
  const infoPanelRef = useCallback((node) => {
    infoRef.current = node;
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);
  useEffect(() => {
    if (!info) return;
    const handleClickOutside = (e) => {
      if (infoRef.current && !infoRef.current.contains(e.target)) setInfo(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [info]);
  const [loading, setLoading] = useState(false);
  const [kmlData] = useState(null);
  const [styles] = useState([]);
  const [icons] = useState([]);
  // const [, setLegendImageUrls] = useState([]);
  const [showKmlData, setShowKmlData] = useState(false);
  const [showComponents, setShowComponents] = useState(false);
  const [activeSection, setActiveSection] = useState("observation");
  const [tabularData, setTabularData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const filterPanelRef = useRef(null);
  const [advisoryData, setAdvisoryData] = useState([]);
  const [oceanWaveTableData, setOceanWaveTableData] = useState([]);
  const [, setIsDataReady] = useState(false); // Declare isDataReady state

  const overviewRef = useRef(null);
  const mapRef = useRef(null);
  const rainfallRef = useRef(null);
  const advisoryRef = useRef(null);
  const obsRef = useRef(null);
  const areaRef = useRef(null);
  const mapInstance = useRef(null);
  const waveRef = useRef(null);

  const [municipalities, setMunicipalities] = useState([]);
  const [allSubMunicipalities,] = useState([]);
  const [allSucos,] = useState([]);
  const [subMunicipalities, setSubMunicipalities] = useState([]);
  const [sucos, setSucos] = useState([]);
  const [selectedMunicipality, setSelectedMunicipality] = useState('');
  const [selectedSubMunicipality, setSelectedSubMunicipality] = useState('');
  const [selectedSuco, setSelectedSuco] = useState('');
  const [adm1Data, setAdm1Data] = useState(null);
  const [adm2Data, setAdm2Data] = useState(null);
  const [adm3Data, setAdm3Data] = useState(null);
  const [zoomFeature, setZoomFeature] = useState(null);
  const [mapBounds, setMapBounds] = useState(null);
  const [initialMapBounds, setInitialMapBounds] = useState(null);
  const [resetTrigger, setResetTrigger] = useState(0);

  const [formData, setFormData] = useState({
    cycloneName: "No selection",
    date: "No selection",
  });
  const [submittedData, setSubmittedData] = useState(null);

  const [forecastDates,] = useState([
    getForecastDate(0),
    getForecastDate(1),
    getForecastDate(2),
  ]);

  const handleMunicipalityChange = (e) => {
    const value = e.target.value;
    setSelectedMunicipality(value);
  };

  const handleSubMunicipalityChange = (e) => {
    const value = e.target.value;
    setSelectedSubMunicipality(value);
  };

  const handleSucoChange = (e) => {
    const value = e.target.value;
    setSelectedSuco(value);
  };

  const handleCycloneFormChange = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const handleCycloneSubmit = () => {
    setSubmittedData(formData);
    setShowKmlData(true);
    setShowComponents(true);
  };

  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);

        const [
          municipalityGeo,
          subMunicipalityGeo,
          sucoGeo,
          allSucoRainfallData,
          munisResp,
        ] = await Promise.all([
          fetch("/DATA/OSMbased_adm1_boundary.json").then((res) => res.json()),
          fetch("/DATA/OSMbased_adm2_boundary.json").then((res) => res.json()),
          fetch("/DATA/OSMbased_adm3_boundary.json").then((res) => res.json()),
          fetchAllForecastMapData("suco"),
          axios.post(`${backendUrl}Municipalities/municipalities_list/`),
        ]);

        const processedSucoRainfallForecasts = processForecastDataForDays(
          allSucoRainfallData,
          forecastDates
        );

        const tabularDataFromSuco = sucoGeo?.features
          ? sucoGeo.features.map((feature) => {
              const admCode = feature.properties.ADM_Code;
              return {
                municipality: feature.properties.ADM1,
                PostAdmin: feature.properties.ADM2,
                Suco: feature.properties.ADM3,
                rainfallDay1: processedSucoRainfallForecasts[forecastDates[0]]?.[admCode] || null,
                rainfallDay2: processedSucoRainfallForecasts[forecastDates[1]]?.[admCode] || null,
                rainfallDay3:
                  processedSucoRainfallForecasts[forecastDates[2]]?.[admCode] ||
                  null,
              };
            })
          : [];

        setAdm1Data(municipalityGeo);
        setAdm2Data(subMunicipalityGeo);
        setAdm3Data(sucoGeo);

        setTabularData(tabularDataFromSuco);
        setFilteredData(tabularDataFromSuco);
        setMunicipalities(munisResp.data);

        if (municipalityGeo && municipalityGeo.features && municipalityGeo.features.length > 0) {
          const geoJsonLayer = L.geoJSON(municipalityGeo);
          const initialBounds = geoJsonLayer.getBounds();
          setInitialMapBounds(initialBounds);
          setMapBounds(initialBounds);
        }

      } catch (error) {
        console.error("Error loading initial data:", error);
        alert("Failed to load initial data. Please try again.");
      } finally {
        setLoading(false);
        setIsDataReady(true);
      }
    };
    initializeData();
  }, [forecastDates]);

  useEffect(() => {
    const fetchAdvisoryData = async () => {
      try {
        const response = await axios.get(`${backendUrl}cyclone/cyclone-advisories/`);
        setAdvisoryData(response.data);
      } catch (error) {
        console.error("Error fetching cyclone advisories:", error);
      }
    };
    fetchAdvisoryData();
  }, []);

  useEffect(() => {
    const fetchOceanData = async () => {
      try {
        const response = await axios.get(`https://tl-osfas.rimes.int/api/weatherforecasts?sourceId=1`);
        if (Array.isArray(response.data.data)) {
          const filteredData = response.data.data.filter(
            (item) => item.parameter.id === 3
          );
          const uniqueLocations = filteredData.reduce((acc, item) => {
            if (!acc[item.location.id] || moment(item.valid_time).isAfter(acc[item.location.id].valid_time)) {
              acc[item.location.id] = {
                locationName: item.location.name,
                municipalityName: item.location.region,
                validTime: item.valid_time,
                value: item.value,
              };
            }
            return acc;
          }, {});
          const latestForecasts = Object.values(uniqueLocations).map(loc => ({
            locationName: loc.locationName,
            municipalityName: loc.municipalityName,
            validTime: loc.validTime,
            value: loc.value,
          }));
          setOceanWaveTableData(latestForecasts);
        }
      } catch (error) {
        console.error("Error fetching ocean data for table:", error);
      }
    };
    fetchOceanData();
  }, []);

  useEffect(() => {
    if (selectedMunicipality) {
      axios
        .post(`${backendUrl}Municipalities/sub_municipalities_list/`, {
          adm2_code: selectedMunicipality,
        })
        .then((response) => {
          setSubMunicipalities(response.data);
          setSucos([]);
          setSelectedSubMunicipality('');
          setSelectedSuco('');
        })
        .catch((error) => {
          console.error('Error fetching sub-municipalities:', error);
        });
    } else {
      setSubMunicipalities([]);
      setSucos([]);
      setSelectedSubMunicipality('');
      setSelectedSuco('');
    }
  }, [selectedMunicipality]);

  useEffect(() => {
    if (selectedSubMunicipality) {
      axios
        .post(`${backendUrl}Municipalities/suco_list/`, {
          adm2_code: selectedSubMunicipality,
        })
        .then((response) => {
          setSucos(response.data);
          setSelectedSuco('');
        })
        .catch((error) => console.error('Error fetching sucos:', error));
    } else {
      setSucos([]);
      setSelectedSuco('');
    }
  }, [selectedSubMunicipality]);

  useEffect(() => {
    let featureToZoom = null;
    if (selectedSuco && adm3Data) {
      featureToZoom = adm3Data.features.find((f) => f.properties.ADM_Code === selectedSuco);
    } else if (selectedSubMunicipality && adm2Data) {
      featureToZoom = adm2Data.features.find((f) => f.properties.ADM_Code === selectedSubMunicipality);
    } else if (selectedMunicipality && adm1Data) {
      featureToZoom = adm1Data.features.find((f) => f.properties.ADM_Code === selectedMunicipality);
    }
    setZoomFeature(featureToZoom);
  }, [selectedMunicipality, selectedSubMunicipality, selectedSuco, adm1Data, adm2Data, adm3Data]);

  useMemo(() => {
    let data = tabularData;
    let munisList = municipalities;
    let subMunisList = allSubMunicipalities;
    let sucoList = allSucos;
    
    if (selectedMunicipality) {
      const muniName = munisList.find(m => m.adm1_code === selectedMunicipality)?.adm1;
      data = data.filter(item => item.municipality === muniName);
    }
    
    if (selectedSubMunicipality) {
      const subMuniName = subMunisList.find(s => s.adm2_code === selectedSubMunicipality)?.adm2;
      data = data.filter(item => item.PostAdmin === subMuniName);
    }
    
    if (selectedSuco) {
      const sucoName = sucoList.find(s => s.adm3_code === selectedSuco)?.adm3;
      data = data.filter(item => item.Suco === sucoName);
    }

    setFilteredData(data);
  }, [selectedMunicipality, selectedSubMunicipality, selectedSuco, tabularData, municipalities, allSubMunicipalities, allSucos]);

  useEffect(() => {
    if (resetTrigger > 0) {
      setSelectedMunicipality('');
      setSelectedSubMunicipality('');
      setSelectedSuco('');
      setZoomFeature(null);
      setFilteredData(tabularData);
      if (initialMapBounds) {
        setMapBounds(null);
        setTimeout(() => setMapBounds(initialMapBounds), 0);
      }
    }
  }, [resetTrigger, initialMapBounds, tabularData]);

  const selectedGeojsonStyle = (feature) => {
    if (feature.properties.ADM_Code) {
      if (feature.properties.ADM_Code === selectedSuco) {
        return { color: "#333333", weight: 1, fillOpacity: 0 };
      }
    }
    if (feature.properties.ADM2_Code) {
      if (feature.properties.ADM2_Code === selectedSubMunicipality) {
        return { color: "#333333", weight: 3, fillOpacity: 0 };
      }
    }
    if (feature.properties.ADM1_Code) {
      if (feature.properties.ADM1_Code === selectedMunicipality) {
        return { color: "#333333", weight: 3, fillOpacity: 0 };
      }
    }
    return { color: "#333333", weight: 0.5, opacity: 1, fillOpacity: 0 };
  };

  const handleLanguageSelectAndGenerate = (selectedLanguage) => {
    setShowFilterPanel(false);
    setShowLanguageModal(false);
    captureAndGeneratePDF(selectedLanguage);
  };

  const captureAndGeneratePDF = async (language) => {
    if (
      !mapRef.current ||
      !overviewRef.current ||
      !rainfallRef.current ||
      !obsRef.current ||
      !areaRef.current ||
      !waveRef.current ||
      !advisoryRef.current ||
      tabularData.length === 0
    ) {
      alert(
        "Failed to generate PDF. Please ensure all data is loaded and form is submitted correctly."
      );
      return;
    }

    setLoading(true);

    try {
      const pdf = new jsPDF("portrait", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const sideMargin = 10;
      const topMargin = 10;
      const footerHeight = 10;
      const marginBottom = 10;
      const bannerHeight = 20;
      const headerHeight = topMargin + bannerHeight + 5;
      const availablePageHeight =
        pageHeight - headerHeight - footerHeight - marginBottom;
      let currentY;

      const t = translations[language];
      const rainfallT = rainfallCharacteristicsTranslations[language];
      const oceanTableHeaders = t.oceanTableHeaders;

      let bannerImage;
      const bannerPath =
        language === "en" ? "/pdf/banner.png" : "/pdf/tetumbanner.png";
      try {
        bannerImage = await loadImage(bannerPath);
      } catch (error) {
        console.warn(`Failed to load ${bannerPath}:`, error);
        bannerImage = null;
      }

      const addPageWithHeader = () => {
        pdf.addPage();
        return addBanner(pdf, bannerImage, sideMargin, topMargin, pageWidth);
      };

      currentY = addBanner(pdf, bannerImage, sideMargin, topMargin, pageWidth);
      currentY += 5;

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(14);
      pdf.setTextColor(0, 0, 0);
      if (currentY + 10 > availablePageHeight + headerHeight) {
        currentY = addPageWithHeader();
      }
      pdf.text(t.warningTitle, pageWidth / 2, currentY, { align: "center" });
      pdf.setFont("helvetica", "normal");
      currentY += 10;

      const now = new Date();
      const formattedDate = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Dili",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      }).format(now);

      const [datePart, timePartRaw] = formattedDate.split(", ");
      const [day, month, year] = datePart.split("/");
      const timeMatch = timePartRaw.match(
        /(\d{2}):(\d{2}):(\d{2})\s*(AM|PM)?/i
      );
      const hour = timeMatch ? timeMatch[1] : "00";
      const minute = timeMatch ? timeMatch[2] : "00";
      const second = timeMatch ? timeMatch[3] : "00";
      const period =
        timeMatch && timeMatch[4] ? timeMatch[4].toUpperCase() : "";

      const formattedTimorDate = `${year}-${month}-${day} ${hour}:${minute}:${second} ${period} East Timor Time (TLT)`;

      pdf.setFontSize(10);
      if (currentY + 6 > availablePageHeight + headerHeight) {
        currentY = addPageWithHeader();
      }

      pdf.setFont("helvetica", "bold");
      const advisoryNumberLabel = t.advisoryNumber;
      pdf.text(advisoryNumberLabel, sideMargin, currentY);
      pdf.setFont("helvetica", "normal");
      pdf.text(
        "1",
        sideMargin + pdf.getTextWidth(advisoryNumberLabel) + 2,
        currentY
      );
      pdf.setFont("helvetica", "bold");
      pdf.text(`${t.observation}`, sideMargin, currentY + 6);
      const obsWidth = pdf.getTextWidth(`${t.observation} `);
      pdf.setFont("helvetica", "normal");
      pdf.text(
        submittedData?.cycloneName || "N/A",
        sideMargin + obsWidth,
        currentY + 6
      );
      pdf.setFont("helvetica", "bold");
      const issuedAtLabel = t.issuedAt;
      const rightText = `${issuedAtLabel}${formattedTimorDate}`;
      const rightTextWidth = pdf.getTextWidth(rightText);
      const rightX = pageWidth - sideMargin - rightTextWidth;
      pdf.text(issuedAtLabel, rightX, currentY);
      pdf.setFont("helvetica", "normal");
      pdf.text(
        formattedTimorDate,
        rightX + pdf.getTextWidth(issuedAtLabel) + 2,
        currentY
      );
      currentY += 12;

      if (currentY + 6 > availablePageHeight + headerHeight) {
        currentY = addPageWithHeader();
      }
      pdf.setFont("helvetica", "bold");
      const issuedByLabel = t.issuedBy;
      pdf.text(issuedByLabel, sideMargin, currentY);
      pdf.setFont("helvetica", "normal");
      pdf.text(
        "National Directorate of Meteorology and Geophysics",
        sideMargin + pdf.getTextWidth(issuedByLabel) + 2,
        currentY
      );
      currentY += 6;

      currentY += 5;
      let overviewImgHeight = 0;
      if (overviewRef.current) {
        const tempOverviewBlob = await domtoimage.toBlob(overviewRef.current, {
          width: overviewRef.current.offsetWidth,
          height: overviewRef.current.offsetHeight,
          style: { background: "white" },
          quality: 0.95,
        });
        const tempOverviewUrl = URL.createObjectURL(tempOverviewBlob);
        const tempOverviewImg = await loadImage(tempOverviewUrl);
        const availableWidth = pageWidth - 2 * sideMargin;
        overviewImgHeight =
          (tempOverviewImg.height * availableWidth) / tempOverviewImg.width;
        URL.revokeObjectURL(tempOverviewUrl);
      }

      if (
        currentY + 10 + overviewImgHeight + 5 >
        availablePageHeight + headerHeight
      ) {
        currentY = addPageWithHeader();
      }
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "bold");
      pdf.setFillColor(202, 217, 239);
      pdf.rect(sideMargin, currentY - 8, pageWidth - 2 * sideMargin, 10, "F");
      pdf.setTextColor(0, 0, 0);
      pdf.text(t.overview, sideMargin + 2, currentY - 2, { align: "left" });
      pdf.setTextColor(0, 0, 0);
      currentY += 5;

      if (overviewRef.current) {
        const overviewBlob = await domtoimage.toBlob(overviewRef.current, {
          width: overviewRef.current.offsetWidth,
          height: overviewRef.current.offsetHeight,
          style: { background: "white" },
          quality: 0.95,
        });
        const overviewUrl = URL.createObjectURL(overviewBlob);
        const overviewImg = await loadImage(overviewUrl);
        const availableWidth = pageWidth - 2 * sideMargin;
        const actualOverviewHeight =
          (overviewImg.height * availableWidth) / overviewImg.width;
        pdf.addImage(
          overviewUrl,
          "PNG",
          sideMargin,
          currentY,
          availableWidth,
          actualOverviewHeight
        );
        currentY += actualOverviewHeight + 5;
        URL.revokeObjectURL(overviewUrl);
      } else {
        console.warn("No Overview component provided for PDF");
      }
      currentY += 5;

      // let mapImgHeight = 0;
      if (mapRef.current) {
        const infoButton = mapRef.current.querySelector(".info-button");
        const infoPopup = mapRef.current.querySelector(".leaflet-popup");
        const reportButton = mapRef.current.querySelector(".report-buttons");
        const filterToggleButton = mapRef.current.querySelector("[data-filter-toggle]");

        const originalStyles = {
          reportButton: reportButton ? reportButton.style.display : null,
          filterToggleButton: filterToggleButton ? filterToggleButton.style.display : null,
        };

        if (reportButton) reportButton.style.display = "none";
        if (filterToggleButton) filterToggleButton.style.display = "none";
        const originalInfoButtonStyle = infoButton
          ? infoButton.style.display
          : null;
        const originalInfoPopupStyle = infoPopup
          ? infoPopup.style.display
          : null;
        if (infoButton) infoButton.style.display = "none";
        if (infoPopup) infoPopup.style.display = "none";

        const tempMapBlob = await domtoimage.toBlob(mapRef.current, {
          width: 1000,
          height: mapRef.current.offsetHeight,
          style: { background: "white" },
          quality: 0.95,
        });

        if (reportButton && originalStyles.reportButton !== null) reportButton.style.display = originalStyles.reportButton;
        if (filterToggleButton && originalStyles.filterToggleButton !== null) filterToggleButton.style.display = originalStyles.filterToggleButton;

        if (infoButton && originalInfoButtonStyle !== null) {
          infoButton.style.display = originalInfoButtonStyle;
        }
        if (infoPopup && originalInfoPopupStyle !== null) {
          infoPopup.style.display = originalInfoPopupStyle;
        }

        const mapUrl = URL.createObjectURL(tempMapBlob);
        const mapImg = await loadImage(mapUrl);
        const availableWidth = pageWidth - 2 * sideMargin;
        const actualMapHeight = (mapImg.height * availableWidth) / mapImg.width;
        pdf.addImage(
          mapUrl,
          "PNG",
          sideMargin,
          currentY,
          availableWidth,
          actualMapHeight
        );
        currentY += actualMapHeight + 5;
        URL.revokeObjectURL(mapUrl);
      } else {
        console.warn("No cyclone map provided for PDF");
      }
      currentY += 5;

      let obsImgHeight = 0;
      if (obsRef.current) {
        const tempObsBlob = await domtoimage.toBlob(obsRef.current, {
          width: obsRef.current.offsetWidth,
          height: obsRef.current.offsetHeight,
          style: { background: "white" },
          quality: 0.95,
        });
        const tempObsUrl = URL.createObjectURL(tempObsBlob);
        const tempObsImg = await loadImage(tempObsUrl);
        const availableWidth = pageWidth - 2 * sideMargin;
        obsImgHeight = (tempObsImg.height * availableWidth) / tempObsImg.width;
        URL.revokeObjectURL(tempObsUrl);
      }

      if (
        currentY + 10 + obsImgHeight + 5 >
        availablePageHeight + headerHeight
      ) {
        currentY = addPageWithHeader();
      }
      currentY += 10;
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "bold");
      pdf.setFillColor(202, 217, 239);
      pdf.rect(sideMargin, currentY - 8, pageWidth - 2 * sideMargin, 10, "F");
      pdf.setTextColor(0, 0, 0);
      pdf.text(t.observation, sideMargin + 2, currentY - 2, { align: "left" });
      pdf.setTextColor(0, 0, 0);
      currentY += 5;

      if (obsRef.current) {
        const observationBlob = await domtoimage.toBlob(obsRef.current, {
          width: obsRef.current.offsetWidth,
          height: obsRef.current.offsetHeight,
          style: { background: "white" },
          quality: 0.95,
        });
        const observationUrl = URL.createObjectURL(observationBlob);
        const observationImg = await loadImage(observationUrl);
        const availableWidth = pageWidth - 2 * sideMargin;
        const actualObservationHeight =
          (observationImg.height * availableWidth) / observationImg.width;
        pdf.addImage(
          observationUrl,
          "PNG",
          sideMargin,
          currentY,
          availableWidth,
          actualObservationHeight
        );
        currentY += actualObservationHeight + 5;
        URL.revokeObjectURL(observationUrl);
      } else {
        console.warn("No Observation component provided for PDF");
      }
      currentY += 5;

      let rainfallMapImgHeight = 0;
      if (rainfallRef.current) {
        const rainfallContainer =
          rainfallRef.current.querySelector(".two-map-container");
        if (rainfallContainer) {
          const tempRainfallBlob = await domtoimage.toBlob(rainfallContainer, {
            width: rainfallContainer.offsetWidth,
            height: rainfallContainer.offsetHeight,
            style: { background: "white" },
            quality: 0.95,
          });
          const tempRainfallUrl = URL.createObjectURL(tempRainfallBlob);
          const tempRainfallImg = await loadImage(tempRainfallUrl);
          const availableWidth = pageWidth - 2 * sideMargin;
          rainfallMapImgHeight =
            (tempRainfallImg.height * availableWidth) / tempRainfallImg.width;
          URL.revokeObjectURL(tempRainfallUrl);
        }
      }

      const rainfallTableData = filteredData
        .sort(
          (a, b) =>
            a.municipality.localeCompare(b.municipality) ||
            a.PostAdmin.localeCompare(b.PostAdmin) ||
            a.Suco.localeCompare(b.Suco)
        )
        .map((item, index) => {
          const charDay1 = getCharacteristic(item.rainfallDay1);
          const charDay2 = getCharacteristic(item.rainfallDay2);
          const charDay3 = getCharacteristic(item.rainfallDay3);
          return [
            index + 1,
            item.municipality,
            item.PostAdmin,
            item.Suco,
            item.rainfallDay1 !== null
              ? parseFloat(item.rainfallDay1).toFixed(2)
              : "N/A",
            {
              content: rainfallT[charDay1],
              styles: { fillColor: characteristicColors[charDay1] },
            },
            item.rainfallDay2 !== null
              ? parseFloat(item.rainfallDay2).toFixed(2)
              : "N/A",
            {
              content: rainfallT[charDay2],
              styles: { fillColor: characteristicColors[charDay2] },
            },
            item.rainfallDay3 !== null
              ? parseFloat(item.rainfallDay3).toFixed(2)
              : "N/A",
            {
              content: rainfallT[charDay3],
              styles: { fillColor: characteristicColors[charDay3] },
            },
          ];
        });

      const estimatedRainfallTableHeight = rainfallTableData.length * 8 + 15;

      if (
        currentY +
          10 +
          rainfallMapImgHeight +
          5 +
          estimatedRainfallTableHeight +
          10 >
        availablePageHeight + headerHeight
      ) {
        currentY = addPageWithHeader();
      }
      currentY += 10;
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "bold");
      pdf.setFillColor(202, 217, 239);
      pdf.rect(sideMargin, currentY - 8, pageWidth - 2 * sideMargin, 10, "F");
      pdf.setTextColor(0, 0, 0);
      pdf.text(t.rainfallAlertMap, sideMargin + 2, currentY - 2, {
        align: "left",
      });
      pdf.setTextColor(0, 0, 0);
      currentY += 5;

      if (rainfallRef.current) {
        const rainfallContainer =
          rainfallRef.current.querySelector(".two-map-container");
        if (rainfallContainer) {
          const rainfallBlob = await domtoimage.toBlob(rainfallContainer, {
            width: rainfallContainer.offsetWidth,
            height: rainfallContainer.offsetHeight,
            style: { background: "white" },
            quality: 0.95,
          });
          const rainfallUrl = URL.createObjectURL(rainfallBlob);
          const rainfallImg = await loadImage(rainfallUrl);
          const availableWidth = pageWidth - 2 * sideMargin;
          const actualRainfallHeight =
            (rainfallImg.height * availableWidth) / rainfallImg.width;
          pdf.addImage(
            rainfallUrl,
            "PNG",
            sideMargin,
            currentY,
            availableWidth,
            actualRainfallHeight
          );
          currentY += actualRainfallHeight + 5;
          URL.revokeObjectURL(rainfallUrl);
        } else {
          console.warn("No two-map-container found in RainfallForecast");
        }
      } else {
        console.warn("No RainfallForecast component provided for PDF");
      }
      currentY += 5;

      const rainfallTableHead = [
        [
          {
            content: t.rainfallTableHeaders[0][0].content,
            rowSpan: 2,
            styles: { halign: "center", valign: "middle" },
          },
          {
            content: t.rainfallTableHeaders[0][1].content,
            rowSpan: 2,
            styles: { halign: "center", valign: "middle" },
          },
          {
            content: t.rainfallTableHeaders[0][2].content,
            rowSpan: 2,
            styles: { halign: "center", valign: "middle" },
          },
          {
            content: t.rainfallTableHeaders[0][3].content,
            rowSpan: 2,
            styles: { halign: "center", valign: "middle" },
          },
          {
            content: getForecastDate(0),
            colSpan: 2,
            styles: { halign: "center" },
          },
          {
            content: getForecastDate(1),
            colSpan: 2,
            styles: { halign: "center" },
          },
          {
            content: getForecastDate(2),
            colSpan: 2,
            styles: { halign: "center" },
          },
        ],
        t.rainfallTableHeaders[1],
      ];

      autoTable(pdf, {
        startY: currentY,
        head: rainfallTableHead,
        body: rainfallTableData,
        theme: "grid",
        headStyles: {
          fillColor: [225, 232, 242],
          halign: "center",
          fontStyle: "bold",
          textColor: "black",
        },
        styles: {
          fontSize: 8,
          cellPadding: 2,
          overflow: "linebreak",
          lineWidth: 0.1,
          lineColor: [150, 150, 150],
        },
        columnStyles: {
          0: { cellWidth: 10, halign: "center", valign: "middle" },
          1: { cellWidth: 22, halign: "left", valign: "middle" },
          2: { cellWidth: 22, halign: "left", valign: "middle" },
          3: { cellWidth: 22, halign: "left", valign: "middle" },
          4: { cellWidth: 17, halign: "center", valign: "middle" },
          5: { cellWidth: 21, halign: "center", valign: "middle" },
          6: { cellWidth: 17, halign: "center", valign: "middle" },
          7: { cellWidth: 21, halign: "center", valign: "middle" },
          8: { cellWidth: 17, halign: "center", valign: "middle" },
          9: { cellWidth: 21, halign: "center", valign: "middle" },
        },
        margin: { left: sideMargin, right: sideMargin, top: headerHeight },
        willDrawPage: (data) => {
          data.settings.startY = addBanner(
            pdf,
            bannerImage,
            sideMargin,
            topMargin,
            pageWidth
          );
        },
        didDrawPage: (data) => {
          currentY = data.cursor.y + 10;
        },
      });
      currentY += 5;

      let impactImgHeight = 0;
      if (areaRef.current) {
        const tempAreaBlob = await domtoimage.toBlob(
          areaRef.current,
          {
            width: areaRef.current.offsetWidth,
            height: areaRef.current.offsetHeight,
            style: { background: "white" },
            quality: 0.95,
          }
        );
        const tempAreaUrl = URL.createObjectURL(tempAreaBlob);
        const tempAreaImg = await loadImage(tempAreaUrl);
        const availableWidth = pageWidth - 2 * sideMargin;
        impactImgHeight =
          (tempAreaImg.height * availableWidth) / tempAreaImg.width;
        URL.revokeObjectURL(tempAreaUrl);
      }

      if (
        currentY + 10 + impactImgHeight + 5 >
        availablePageHeight + headerHeight
      ) {
        currentY = addPageWithHeader();
      }
      currentY += 10;
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "bold");
      pdf.setFillColor(202, 217, 239);
      pdf.rect(sideMargin, currentY - 8, pageWidth - 2 * sideMargin, 10, "F");
      pdf.setTextColor(0, 0, 0);
      pdf.text(t.impact, sideMargin + 2, currentY - 2, { align: "left" });
      pdf.setTextColor(0, 0, 0);
      currentY += 5;

      if (areaRef.current) {
        const impactedAreaBlob = await domtoimage.toBlob(areaRef.current, {
          width: areaRef.current.offsetWidth,
          height: areaRef.current.offsetHeight,
          style: { background: "white" },
          quality: 0.95,
        });
        const impactedAreaUrl = URL.createObjectURL(impactedAreaBlob);
        const impactedAreaImg = await loadImage(impactedAreaUrl);
        const availableWidth = pageWidth - 2 * sideMargin;
        const actualImpactedAreaHeight =
          (impactedAreaImg.height * availableWidth) / impactedAreaImg.width;
        pdf.addImage(
          impactedAreaUrl,
          "PNG",
          sideMargin,
          currentY,
          availableWidth,
          actualImpactedAreaHeight
        );
        currentY += actualImpactedAreaHeight + 5;
        URL.revokeObjectURL(impactedAreaUrl);
      } else {
        console.warn("No Impacted Area component provided for PDF");
      }
      currentY += 5;

      // Ocean section heading and table
      if (currentY + 10 > availablePageHeight + headerHeight) {
        currentY = addPageWithHeader();
      }
      currentY += 10;
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "bold");
      pdf.setFillColor(202, 217, 239);
      pdf.rect(sideMargin, currentY - 8, pageWidth - 2 * sideMargin, 10, "F");
      pdf.setTextColor(0, 0, 0);
      pdf.text(t.ocean, sideMargin + 2, currentY - 2, { align: "left" });
      pdf.setTextColor(0, 0, 0);
      currentY += 5;

      const oceanTableData = oceanWaveTableData.map((item, index) => {
        const waveCategory = getWaveCategory(item.value);
        return [
          index + 1,
          item.locationName,
          item.municipalityName,
          moment.utc(item.validTime).tz('Asia/Dili').format('YYYY-MM-DD HH:mm'),
          item.value !== null ? parseFloat(item.value).toFixed(2) : 'N/A',
          {
            content: waveCategory.label,
            styles: { fillColor: waveCategory.color },
          },
        ];
      });

      autoTable(pdf, {
        startY: currentY,
        head: [oceanTableHeaders],
        body: oceanTableData,
        theme: "grid",
        headStyles: {
          fillColor: [225, 232, 242],
          halign: "center",
          fontStyle: "bold",
          textColor: "black",
        },
        styles: {
          fontSize: 8,
          cellPadding: 2,
          overflow: "linebreak",
          lineWidth: 0.1,
          lineColor: [150, 150, 150],
        },
        columnStyles: {
          0: { cellWidth: 15, halign: "center", valign: "middle" },
          1: { cellWidth: 'auto', halign: "left", valign: "middle" },
          2: { cellWidth: 'auto', halign: "left", valign: "middle" },
          3: { cellWidth: 'auto', halign: "center", valign: "middle" },
          4: { cellWidth: 'auto', halign: "center", valign: "middle" },
          5: { cellWidth: 'auto', halign: "center", valign: "middle" },
        },
        margin: { left: sideMargin, right: sideMargin, top: headerHeight },
        willDrawPage: (data) => {
          data.settings.startY = addBanner(
            pdf,
            bannerImage,
            sideMargin,
            topMargin,
            pageWidth
          );
        },
        didDrawPage: (data) => {
          currentY = data.cursor.y + 10;
        },
      });
      currentY += 5;

      const advisoryTableData = advisoryData.map(item => {
        const headingKey = language === 'en' ? 'englishheading' : 'tetumheading';
        const descriptionKey = language === 'en' ? 'englishdescription' : 'tetumdescription';
        
        const descriptionText = item[descriptionKey].replace(/<ul\s*>/g, '').replace(/<\/ul\s*>/g, '');
        const points = descriptionText.split('<li>').slice(1).map(p => `• ${p.replace(/<\/li>/g, '').trim()}`);
        
        return [
          item[headingKey],
          points.join("\n")
        ];
      });

      let estimatedAdvisoryTableHeight = 0;
      advisoryTableData.forEach((row) => {
        estimatedAdvisoryTableHeight += row[1].split("\n").length * 6 + 10;
      });
      estimatedAdvisoryTableHeight += 15;

      if (
        currentY + 10 + estimatedAdvisoryTableHeight + 10 >
        availablePageHeight + headerHeight
      ) {
        currentY = addPageWithHeader();
      } else {
        currentY += 10;
      }

      pdf.setFontSize(12);
      pdf.setFont("helvetica", "bold");
      pdf.setFillColor(202, 217, 239);
      pdf.rect(sideMargin, currentY - 8, pageWidth - 2 * sideMargin, 10, "F");
      pdf.setTextColor(0, 0, 0);
      pdf.text(t.generalAdvisory, sideMargin + 2, currentY - 2, {
        align: "left",
      });
      pdf.setTextColor(0, 0, 0);
      currentY += 7;

      autoTable(pdf, {
        startY: currentY,
        head: [t.advisoryTableHeaders],
        body: advisoryTableData,
        theme: "grid",
        headStyles: {
          fillColor: [225, 232, 242],
          halign: "left",
          fontStyle: "bold",
          textColor: "black",
        },
        styles: {
          fontSize: 9,
          cellPadding: { top: 3, right: 3, bottom: 3, left: 5 },
          overflow: "linebreak",
          lineHeight: 1.2,
        },
        columnStyles: {
          0: { cellWidth: 60, halign: "left" },
          1: { cellWidth: pageWidth - 2 * sideMargin - 60, halign: "left" },
        },
        margin: { left: sideMargin, right: sideMargin, top: headerHeight },
        willDrawPage: (data) => {
          data.settings.startY = addBanner(
            pdf,
            bannerImage,
            sideMargin,
            topMargin,
            pageWidth
          );
        },
        didDrawPage: (data) => {
          currentY = data.cursor.y + 10;
        },
      });
      currentY += 5;

      const noteTextLines = pdf.splitTextToSize(
        t.noteText,
        pageWidth - 2 * sideMargin
      );
      const estimatedNoteHeight = noteTextLines.length * 6 + 12;

      if (currentY + estimatedNoteHeight > availablePageHeight + headerHeight) {
        currentY = addPageWithHeader();
      }
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "bold");
      pdf.text(t.noteLabel, sideMargin, currentY);
      currentY += 6;
      pdf.setFont("helvetica", "normal");
      noteTextLines.forEach((line) => {
        if (currentY + 6 > availablePageHeight + headerHeight) {
          currentY = addPageWithHeader();
        }
        pdf.text(line, sideMargin, currentY);
        currentY += 6;
      });
      currentY += 4;

      if (currentY + 6 > availablePageHeight + headerHeight) {
        currentY = addPageWithHeader();
      }
      pdf.setFont("helvetica", "bold");
      pdf.text("*****", (pageWidth - pdf.getTextWidth("*****")) / 2, currentY);
      currentY += 6;
      pdf.setFont("helvetica", "normal");

      const pageCount = pdf.internal.getNumberOfPages();
      const footerYOffset = 5;
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.text(
          t.footer,
          pageWidth / 2,
          pageHeight - footerHeight + footerYOffset - 2,
          { align: "center" }
        );
        pdf.text(
          t.page.replace("[i]", i).replace("[pageCount]", pageCount),
          pageWidth / 2,
          pageHeight - footerYOffset + footerYOffset + 3,
          { align: "center" }
        );
      }

      pdf.save("DRRDSS-Cyclone-Advisory.pdf");
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  const isFormIncomplete = Object.values(formData).includes("No selection");

  return (
    <>
      <div className="wind-speed-cyclone-container">
        <div className="mapcontainerall" ref={mapRef}>
          <MapContainer
            center={[INITIAL_VIEW_STATE.latitude, INITIAL_VIEW_STATE.longitude]}
            zoom={INITIAL_VIEW_STATE.zoom}
            style={{ width: "100%", height: "80.5vh" }}
            whenCreated={(map) => {
              mapInstance.current = map;
            }}
          >
            <MapHomeControl />
            <MapBoundsUpdater bounds={mapBounds} />
            {zoomFeature && <ZoomToFeature feature={{type: 'FeatureCollection', features: [zoomFeature]}} />}

            <Box
              sx={{ position: "absolute", top: 10, left: 50, zIndex: 1000 }}
              ref={filterPanelRef}
            >
              <IconButton
                color="primary"
                onClick={() => setShowFilterPanel(!showFilterPanel)}
                data-filter-toggle
              >
                <Box
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "50%",
                    padding: "8px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: 1,
                  }}
                >
                  <FilterAltIcon sx={{ color: "#0d6efd", fontSize: 32 }} />
                </Box>
              </IconButton>

              <AnimatePresence>
                {showFilterPanel && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <div className="FilterPanel">
                      <div className="FilterPanel-container">
                        <div className="dropdowns-row">
                          <Form.Group controlId="formGroupCyclone">
                            <Form.Label className="labeltext">Cyclone Name:</Form.Label>
                            <Form.Select
                              name="cycloneName"
                              value={formData.cycloneName}
                              onChange={handleCycloneFormChange}
                            >
                              <option value="No selection">No selection</option>
                              <option value="Cyclone 1">Cyclone 1</option>
                              <option value="Cyclone 2">Cyclone 2</option>
                              <option value="Cyclone 3">Cyclone 3</option>
                            </Form.Select>
                          </Form.Group>
                          <Form.Group controlId="formGroupDate">
                            <Form.Label className="labeltext">Date:</Form.Label>
                            <Form.Select
                              name="date"
                              value={formData.date}
                              onChange={handleCycloneFormChange}
                            >
                              <option value="No selection">No selection</option>
                              <option value="01/01/2020">01/01/2020</option>
                              <option value="20/01/2021">20/01/2021</option>
                              <option value="30/01/2022">30/01/2022</option>
                            </Form.Select>
                          </Form.Group>
                        </div>
                        
                        <button
                          className="submit-impact"
                          onClick={handleCycloneSubmit}
                          disabled={isFormIncomplete}
                        >
                          Submit
                        </button>
                        
                        <div className="location-filters-container">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <h4 className="dropdown-heading" style={{ margin: 0 }}>Location:</h4>
                            <IconButton
                                onClick={() => setResetTrigger(prev => prev + 1)}
                                sx={{
                                    backgroundColor: 'white',
                                    padding: '5px',
                                    borderRadius: '50%',
                                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                }}
                            >
                                <RestartAltIcon sx={{ color: "#0d6efd", fontSize: 24 }} />
                            </IconButton>
                          </Box>
                          <div className="dropdowns-row">
                            <Form.Group controlId="formGroupMunicipality">
                              <Form.Label className="labeltext">Municipality:</Form.Label>
                              <Form.Select
                                name="municipality"
                                value={selectedMunicipality}
                                onChange={handleMunicipalityChange}
                              >
                                <option value="">Select a municipality</option>
                                {municipalities.map((municipality, index) => (
                                  <option key={index} value={municipality.adm1_code}>
                                    {municipality.adm1}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                            <Form.Group controlId="formGroupSubMunicipality">
                              <Form.Label className="labeltext">Post-Admin:</Form.Label>
                              <Form.Select
                                name="sub_municipality"
                                value={selectedSubMunicipality}
                                onChange={handleSubMunicipalityChange}
                                disabled={!selectedMunicipality}
                              >
                                <option value="">
                                  {selectedMunicipality
                                    ? 'Select a Post-Admin'
                                    : 'Select a municipality first'}
                                </option>
                                {subMunicipalities.map((submunicipality, index) => (
                                  <option key={index} value={submunicipality.adm2_code}>
                                    {submunicipality.adm2}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                            <Form.Group controlId="formGroupSuco">
                              <Form.Label className="labeltext">Suco:</Form.Label>
                              <Form.Select
                                name="suco"
                                value={selectedSuco}
                                onChange={handleSucoChange}
                                disabled={!selectedSubMunicipality}
                              >
                                <option value="">
                                  {selectedSubMunicipality
                                    ? 'Select a suco'
                                    : 'Select a Post-Admin first'}
                                </option>
                                {sucos.map((suco, index) => (
                                  <option key={index} value={suco.adm3_code}>
                                    {suco.adm3}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>

            <div className="icon-with-tooltip">
              <button
                className="report-buttons"
                onClick={() => setShowLanguageModal(true)}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <DescriptionIcon />
                )}{" "}
                <span className="tooltip-text">Generate Report</span>
              </button>
            </div>
            <TileLayer
              url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
              attribution='Map data © Tropical Storm Risk (TSR)'
            />
            {info && (
              <div
                ref={infoPanelRef}
                style={{
                position: "absolute",
                bottom: "70px",
                left: "12px",
                fontSize: "12px",
                zIndex: 1000,
                padding: "10px 14px",
                borderRadius: "10px",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px) brightness(1.1)",
                WebkitBackdropFilter: "blur(10px) brightness(1.1)",
                border: "1px solid rgba(0, 0, 0, 0.08)",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.5)",
                color: "#333333",
                fontWeight: "500",
                maxWidth: "320px",
                lineHeight: "1.4",
              }}
              >
                Information about areas impacted by cyclones is available here,
                including both current and historical track data, as well as
                associated ocean state details.
              </div>
            )}
            <InfoButton onClick={() => setInfo(!info)} />
            <LayersControl position="topright">
              {adm1Data && (
                <LayersControl.Overlay name="Municipality" >
                  <GeoJSON
                    data={adm1Data}
                    style={selectedGeojsonStyle}
                  />
                </LayersControl.Overlay>
              )}

              {adm2Data && (
                <LayersControl.Overlay name="Post-Admin">
                  <GeoJSON
                    data={adm2Data}
                    style={selectedGeojsonStyle}
                  />
                </LayersControl.Overlay>
              )}
              {adm3Data && (
                <LayersControl.Overlay name="Suco" checked={!selectedSuco}>
                  <GeoJSON
                    data={adm3Data}
                    style={selectedGeojsonStyle}
                  />
                </LayersControl.Overlay>
              )}
            </LayersControl>
            {/* <div className="source-note">Source: Tropical Storm Risk (TSR)</div> */}

            {showKmlData && kmlData && (
              <MapViewAdjuster
                features={kmlData.features}
                map={mapInstance.current}
              />
            )}

            {showKmlData &&
              kmlData &&
              kmlData.features.map((feature, index) => {
                if (feature.geometry.type === "Polygon") {
                  const styleEntry = styles.find((s) =>
                    feature.properties.styleUrl?.includes(s.id)
                  );
                  const kmlColorValue = styleEntry?.fillColor || "#FF000000";

                  const alphaHex = kmlColorValue.substring(1, 3);
                  const blueHex = kmlColorValue.substring(3, 5);
                  const greenHex = kmlColorValue.substring(5, 7);
                  const redHex = kmlColorValue.substring(7, 9);

                  const alpha = parseInt(alphaHex, 16) / 255;
                  const red = parseInt(redHex, 16);
                  const green = parseInt(greenHex, 16);
                  const blue = parseInt(blueHex, 16);

                  const rgbaColor = `rgba(${red}, ${green}, ${blue}, ${alpha.toFixed(
                    2
                  )})`;

                  const borderColorFromStyle =
                    styleEntry?.borderColor || "#000000";

                  return (
                    <GeoJSON
                      key={index}
                      data={feature}
                      style={{
                        color: borderColorFromStyle,
                        weight: 2,
                        opacity: 0.8,
                        fillColor: rgbaColor,
                        fillOpacity: alpha,
                      }}
                      eventHandlers={{
                        click: (e) => {
                          e.target
                            .bindPopup(`${feature.properties.name}`)
                            .openPopup();
                        },
                      }}
                    />
                  );
                } else if (feature.geometry.type === "Point") {
                  const iconStyle = icons.find((icon) =>
                    feature.properties.styleUrl?.includes(icon.id)
                  );

                  const icon = L.icon({
                    iconUrl: iconStyle ? iconStyle.iconUrl : "default-icon.png",
                    iconSize: [32, 32],
                  });

                  return (
                    <Marker
                      key={index}
                      position={[
                        feature.geometry.coordinates[1],
                        feature.geometry.coordinates[0],
                      ]}
                      icon={icon}
                    >
                      <Popup>
                        <div>
                          <h3
                            style={{ fontWeight: "bold", textAlign: "center" }}
                          >
                            {feature.properties["name"] || "N/A"}
                          </h3>
                          <table
                            style={{
                              borderCollapse: "collapse",
                              width: "100%",
                              fontSize: "5px",
                            }}
                          >
                            <tbody>
                              {[
                                ["Date", feature.properties["Date"]],
                                [
                                  "Maximum 10-min Sustained Wind",
                                  feature.properties[
                                    "Maximum 10-min Sustained Wind"
                                  ],
                                ],
                                [
                                  "Storm Category",
                                  feature.properties["Storm Category"],
                                ],
                                [
                                  "Storm Position",
                                  feature.properties["Storm Position"],
                                ],
                              ].map(([label, value]) =>
                                value ? (
                                  <tr key={label}>
                                    <td
                                      style={{
                                        border: "1px solid black",
                                        padding: "5px",
                                        fontWeight: "bold",
                                        width: "35%",
                                      }}
                                    >
                                      {label}
                                    </td>
                                    <td
                                      style={{
                                        border: "1px solid black",
                                        padding: "5px",
                                        width: "65%",
                                      }}
                                    >
                                      {value}
                                    </td>
                                  </tr>
                                ) : null
                              )}
                            </tbody>
                          </table>
                        </div>
                      </Popup>
                    </Marker>
                  );
                } else if (feature.geometry.type === "LineString") {
                  const style = styles.find((s) =>
                    feature.properties.styleUrl?.includes(s.id)
                  ) || {
                    borderColor: "#FF0000",
                  };

                  return (
                    <Polyline
                      key={index}
                      positions={feature.geometry.coordinates.map((coord) => [
                        coord[1],
                        coord[0],
                      ])}
                      pathOptions={{
                        color: style.borderColor,
                        weight: 3,
                        opacity: 0.8,
                      }}
                    >
                      <Popup>{feature.properties.name}</Popup>
                    </Polyline>
                  );
                }
                return null;
              })}

            <div className="map-legends intensity-legend">
              <img src="/poslegend.png" alt="Legend" />
            </div>
            <div className="map-legends normal-legend">
              <img src="/catlegendpast.png" alt="Legend" />
            </div>
          </MapContainer>
        </div>
        {showComponents && (
            <div className="show-buttons-container">

            <div className="section-buttons-container">
              <button
                className={`section-button ${
                  activeSection === "observation" ? "active" : ""
                }`}
                onClick={() => setActiveSection("observation")}
              >
                Observation
              </button>
              <button
                className={`section-button ${
                  activeSection === "rainfall" ? "active" : ""
                }`}
                onClick={() => setActiveSection("rainfall")}
              >
                Rainfall Forecast
              </button>
              <button
                className={`section-button ${
                  activeSection === "area" ? "active" : ""
                }`}
                onClick={() => setActiveSection("area")}
              >
                Impacted Areas
              </button>
              <button
                className={`section-button ${
                  activeSection === "wave" ? "active" : ""
                }`}
                onClick={() => setActiveSection("wave")}
              >
                Ocean Wave
              </button>
            </div>

            <div className="section-content-container">
              <div
                ref={overviewRef}
                className={`section-container ${
                  activeSection === "overview"
                    ? "active-section"
                    : "hidden-section"
                }`}
                dangerouslySetInnerHTML={{ __html: translations["en"].overviewText }}
              />

              {activeSection === "observation" && (
                <div ref={obsRef} className="obs-container">
                  <Observation />
                </div>
              )}

              <div
                ref={rainfallRef}
                className={`section-container ${
                  activeSection === "rainfall"
                    ? "active-section"
                    : "hidden-section"
                }`}
              >
                <RainfallForecast tabularData={filteredData} />
              </div>
              <div
                ref={areaRef}
                className={`section-container ${
                  activeSection === "area" ? "active-section" : "hidden-section"
                }`}
              >
                <ImpactedArea
                  selectedMunicipality={selectedMunicipality}
                  selectedSubMunicipality={selectedSubMunicipality}
                  selectedSuco={selectedSuco}
                  municipalities={municipalities}
                  subMunicipalities={subMunicipalities}
                  sucos={sucos}
                  initialData={tabularData}
                />
              </div>
              <div
                ref={waveRef}
                className={`section-container ${
                  activeSection === "wave" ? "active-section" : "hidden-section"
                }`}
              >
                <OceanWave />
              </div>
              <div
                ref={advisoryRef}
                className={`section-container ${
                  activeSection === "advisory"
                    ? "active-section"
                    : "hidden-section"
                }`}
              >
                    <div style={{ padding: "20px" }}>
                    <h3 style={{ marginBottom: "20px" }}>
                        General Advisory for Cyclones
                    </h3>
                    <p>
                        The Disaster Risk Reduction Decision Support System advises the following precautions to ensure safety and mitigate cyclone risks:
                    </p>
                    <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "20px" }}>
                        <thead>
                        <tr style={{ backgroundColor: "#f2f2f2" }}>
                            <th style={{ padding: "10px", border: "1px solid #ddd" }}>Type</th>
                            <th style={{ padding: "10px", border: "1px solid #ddd" }}>Advisory</th>
                        </tr>
                        </thead>
                        <tbody>
                        {advisoryData.map((advisory, index) => (
                            <tr key={index}>
                            <td style={{ padding: "10px", border: "1px solid #ddd", fontWeight: "bold", verticalAlign: "top" }}>
                                {advisory.englishheading}
                            </td>
                            <td style={{ padding: "10px", border: "1px solid #ddd" }}>
                                <ul style={{ margin: 0, padding: 0, listStyleType: "disc", paddingLeft: "20px" }}>
                                {advisory.englishdescription.split("<li>").slice(1).map((point, i) => (
                                    <li key={i}>{point.replace(/<\/li>/g, '').trim()}</li>
                                ))}
                                </ul>
                            </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    </div>
              </div>
            </div>
            </div>
        )}
      </div>
      <Modal
        show={showLanguageModal}
        onHide={() => setShowLanguageModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "18px", color: "#333" }}>
            🌐 Select Report Language
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ textAlign: "center" }}>
          <Button
            onClick={() => handleLanguageSelectAndGenerate("en")}
            style={{
              padding: "10px 18px",
              borderRadius: "6px",
              backgroundColor: "#007bff",
              color: "#fff",
              border: "none",
              cursor: "pointer",
              marginRight: "12px",
            }}
          >
            English
          </Button>
          <Button
            onClick={() => handleLanguageSelectAndGenerate("tetum")}
            style={{
              padding: "10px 18px",
              borderRadius: "6px",
              backgroundColor: "#28a745",
              color: "#fff",
              border: "none",
              cursor: "pointer",
            }}
          >
            Tetum
          </Button>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowLanguageModal(false)}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ImpactForecast;